import { useEffect, useMemo, useState } from "react";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { useAuth } from "../providers/AuthProvider";
import { createTmaApiClient } from "../services/tmaApiClient";
import { extractPayloadMessage } from "../utils/apiPayload";
import { isUuid } from "../utils/uuid";
import { formatWallClockDateTime } from "../utils/wallClockDate";

type ScheduleStatus = "idle" | "loading" | "ready" | "empty" | "error";
type BookingCancelStatus = "idle" | "submitting" | "success" | "error";

interface AttendanceLessonCell {
  lessonId: string;
  markLabel: string;
}

interface AttendanceStudentRow {
  id: string;
  cells: AttendanceLessonCell[];
}

interface AttendanceLesson {
  id: string;
  lessonNo: number;
  date: string | null;
  label: string;
}

interface AttendanceMatrixPayload {
  flowNo: string;
  generatedAt: string;
  lessons: AttendanceLesson[];
  students: AttendanceStudentRow[];
}

interface StudentScheduleLessonApi {
  id: string;
  startsAt: string | null;
  lessonNo?: number | null;
  flowNo: string;
  topic: string | null;
  attendance?: {
    markLabel?: string | null;
    status?: string | null;
  } | null;
}

interface StudentScheduleApiPayload {
  generatedAt: string;
  lessons: StudentScheduleLessonApi[];
}

interface ScheduleLessonItem {
  id: string;
  lessonNo: number | null;
  isoDate: string | null;
  dateLabel: string;
  timeLabel: string;
  flowNo: string;
  topic: string;
  attendanceLabel: string | null;
  makeupStatus: string | null;
  offeredSlot: string | null;
  makeupRequestId: string | null;
}

interface MakeupRequestSummary {
  id: string;
  status: string;
  offeredSlot: string | null;
  lessonNo: number;
}

type AttendanceDisplayStatus =
  "Присутствовал" | "Отсутствовал" | "Записан на отработку" | "Отработал";
type ScheduleCardTone = "upcoming" | "past" | "missed" | "makeup";

interface ScheduleProps {
  onNavigateToMakeup?: (lessonNo: number | null) => void;
}

const extractLessonNoFromTopic = (
  topic: string | null | undefined,
): number | null => {
  if (!topic) {
    return null;
  }

  const match = topic.match(/(\d+)/);
  if (!match) {
    return null;
  }

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const isLessonInPast = (isoDate: string | null): boolean => {
  if (!isoDate) {
    return false;
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return false;
  }

  return date.getTime() <= Date.now();
};

const normalizeAttendanceStatus = (
  rawLabel: string | null,
  makeupStatus: string | null,
): AttendanceDisplayStatus => {
  const normalizedMakeupStatus = String(makeupStatus ?? "")
    .trim()
    .toLowerCase();

  if (normalizedMakeupStatus === "booked") {
    return "Записан на отработку";
  }

  if (normalizedMakeupStatus === "done") {
    return "Отработал";
  }

  if (
    normalizedMakeupStatus === "open" ||
    normalizedMakeupStatus === "canceled"
  ) {
    return "Отсутствовал";
  }

  const normalized = String(rawLabel ?? "")
    .trim()
    .toLowerCase();

  if (
    normalized.includes("отработал") ||
    normalized === "о" ||
    normalized === "o" ||
    normalized === "done" ||
    normalized === "completed"
  ) {
    return "Отработал";
  }

  if (
    normalized.includes("записан на отработку") ||
    normalized.includes("на отработку") ||
    normalized.includes("booked")
  ) {
    return "Записан на отработку";
  }

  if (!normalized) {
    return "Присутствовал";
  }

  if (
    normalized === "н" ||
    normalized === "absent" ||
    normalized.includes("отсутств")
  ) {
    return "Отсутствовал";
  }

  if (normalized === "+" || normalized === "present") {
    return "Присутствовал";
  }

  return "Присутствовал";
};

const resolveScheduleCardTone = (
  isPastLesson: boolean,
  attendanceStatus: AttendanceDisplayStatus,
): ScheduleCardTone => {
  if (!isPastLesson) {
    return "upcoming";
  }

  if (
    attendanceStatus === "Отсутствовал" ||
    attendanceStatus === "Записан на отработку"
  ) {
    return "missed";
  }

  if (attendanceStatus === "Отработал") {
    return "makeup";
  }

  return "past";
};

const canUseLegacyMatrixFallback = (
  status: number | null,
  responsePayload: unknown,
  sessionToken: string,
): boolean => {
  const message = extractPayloadMessage(responsePayload, "").toLowerCase();

  if (status === 404 && message.includes("cannot get /students/me/schedule")) {
    return true;
  }

  if (status === 401 && sessionToken.startsWith("mock.")) {
    return true;
  }

  return false;
};

const formatLessonDateTime = (
  isoDate: string | null,
): { dateLabel: string; timeLabel: string } => {
  if (!isoDate) {
    return {
      dateLabel: "Дата уточняется",
      timeLabel: "Время уточняется",
    };
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return {
      dateLabel: isoDate,
      timeLabel: "Время уточняется",
    };
  }

  return {
    dateLabel: new Intl.DateTimeFormat("ru-RU", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date),
    timeLabel: new Intl.DateTimeFormat("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date),
  };
};

const buildScheduleLessons = (
  matrix: AttendanceMatrixPayload,
  studentId: string,
): {
  items: ScheduleLessonItem[];
  emptyReason?: string;
  hasStudentRow: boolean;
} => {
  if (!matrix.lessons?.length) {
    return {
      items: [],
      emptyReason: "Для вашего потока пока нет уроков в расписании.",
      hasStudentRow: false,
    };
  }

  const studentRow = matrix.students?.find(
    (student) => student.id === studentId,
  );
  const cellsByLesson = new Map(
    (studentRow?.cells ?? []).map((cell) => [cell.lessonId, cell]),
  );

  const items = matrix.lessons.map((lesson) => {
    const dateTime = formatLessonDateTime(lesson.date);
    const cell = cellsByLesson.get(lesson.id);
    const attendanceLabel =
      cell?.markLabel?.trim() && cell.markLabel !== "-" ? cell.markLabel : null;

    return {
      id: lesson.id,
      lessonNo: lesson.lessonNo,
      isoDate: lesson.date,
      dateLabel: dateTime.dateLabel,
      timeLabel: dateTime.timeLabel,
      flowNo: matrix.flowNo,
      topic: lesson.label?.trim() || `Урок ${lesson.lessonNo}`,
      attendanceLabel,
      makeupStatus: null,
      offeredSlot: null,
      makeupRequestId: null,
    };
  });

  return {
    items,
    hasStudentRow: Boolean(studentRow),
  };
};

const buildScheduleLessonsFromStudentApi = (
  payload: StudentScheduleApiPayload,
): ScheduleLessonItem[] =>
  (payload.lessons ?? []).map((lesson) => {
    const dateTime = formatLessonDateTime(lesson.startsAt);
    const markLabel =
      lesson.attendance?.markLabel ?? lesson.attendance?.status ?? null;
    const attendanceLabel = markLabel?.trim() ? markLabel : null;
    const lessonNo =
      typeof lesson.lessonNo === "number"
        ? lesson.lessonNo
        : extractLessonNoFromTopic(lesson.topic);

    return {
      id: lesson.id,
      lessonNo,
      isoDate: lesson.startsAt,
      dateLabel: dateTime.dateLabel,
      timeLabel: dateTime.timeLabel,
      flowNo: lesson.flowNo,
      topic: lesson.topic?.trim() || "Тема уточняется",
      attendanceLabel,
      makeupStatus: null,
      offeredSlot: null,
      makeupRequestId: null,
    };
  });

const Schedule = ({ onNavigateToMakeup }: ScheduleProps) => {
  const auth = useAuth();
  const [status, setStatus] = useState<ScheduleStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [emptyMessage, setEmptyMessage] = useState<string>(
    "Расписание пока пустое.",
  );
  const [dataNote, setDataNote] = useState<string | null>(null);
  const [scheduleItems, setScheduleItems] = useState<ScheduleLessonItem[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [bookingDetailsItem, setBookingDetailsItem] =
    useState<ScheduleLessonItem | null>(null);
  const [bookingCancelStatus, setBookingCancelStatus] =
    useState<BookingCancelStatus>("idle");
  const [bookingCancelMessage, setBookingCancelMessage] = useState<
    string | null
  >(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const apiClient = useMemo(
    () => createTmaApiClient(auth.session),
    [auth.session],
  );

  const enrichScheduleWithMakeups = async (
    items: ScheduleLessonItem[],
  ): Promise<ScheduleLessonItem[]> => {
    if (!items.length || auth.status !== "authenticated" || !auth.session) {
      return items;
    }

    const flowNo =
      auth.session.user.flowNo?.trim() || items[0]?.flowNo || undefined;
    if (!flowNo) {
      return items;
    }

    const rawStudentId = auth.session.user.id?.trim();
    const resolvedStudentId = isUuid(rawStudentId) ? rawStudentId : undefined;
    const resolvedStudentName =
      !resolvedStudentId && auth.session.user.name?.trim()
        ? auth.session.user.name.trim()
        : undefined;

    const makeupsResult = await apiClient.request({
      method: "GET",
      path: "/makeups",
      query: {
        flowNo,
        studentId: resolvedStudentId,
        studentName: resolvedStudentName,
      },
    });

    if (!makeupsResult.ok || !Array.isArray(makeupsResult.responsePayload)) {
      return items;
    }

    const requests = makeupsResult.responsePayload
      .map((raw) => {
        if (!raw || typeof raw !== "object") {
          return null;
        }

        const source = raw as Record<string, unknown>;
        const id = String(source.id ?? "").trim();
        const rawStatus = String(source.status ?? "").trim();
        const lessonNo =
          typeof source.lessonNo === "number" ? source.lessonNo : null;
        const offeredSlot =
          typeof source.offeredSlot === "string" ? source.offeredSlot : null;
        const status =
          rawStatus.toLowerCase() === "open" && offeredSlot
            ? "booked"
            : rawStatus;

        if (!id || !status || lessonNo === null) {
          return null;
        }

        return {
          id,
          status,
          lessonNo,
          offeredSlot,
        };
      })
      .filter((item): item is MakeupRequestSummary => Boolean(item));

    if (!requests.length) {
      return items;
    }

    const rankStatus = (status: string): number => {
      const normalized = status.toLowerCase();
      if (normalized === "booked") return 3;
      if (normalized === "done") return 2;
      if (normalized === "open") return 1;
      return 0;
    };

    const bestByLessonNo = new Map<number, MakeupRequestSummary>();
    for (const request of requests) {
      const current = bestByLessonNo.get(request.lessonNo);
      if (!current || rankStatus(request.status) > rankStatus(current.status)) {
        bestByLessonNo.set(request.lessonNo, request);
      }
    }

    return items.map((item) => {
      if (item.lessonNo === null) {
        return item;
      }

      const request = bestByLessonNo.get(item.lessonNo);
      if (!request) {
        return item;
      }

      return {
        ...item,
        makeupStatus: request.status,
        offeredSlot: request.offeredSlot,
        makeupRequestId: request.id,
      };
    });
  };

  const loadSchedule = async () => {
    if (auth.status !== "authenticated" || !auth.session) {
      setStatus("error");
      setErrorMessage(
        "Расписание недоступно: отсутствует активная сессия студента.",
      );
      return;
    }

    if (auth.session.user.role !== "student") {
      setStatus("error");
      setErrorMessage("Расписание доступно только для роли студента.");
      return;
    }

    setStatus("loading");
    setErrorMessage(null);
    setDataNote(null);
    setBookingDetailsItem(null);
    setGeneratedAt(null);
    setScheduleItems([]);

    try {
      const studentScheduleResult = await apiClient.request({
        method: "GET",
        path: "/students/me/schedule",
      });

      if (studentScheduleResult.ok) {
        const rawPayload = studentScheduleResult.responsePayload;
        if (!rawPayload || typeof rawPayload !== "object") {
          setStatus("error");
          setErrorMessage(
            "API вернул пустой или некорректный payload расписания.",
          );
          return;
        }

        const schedulePayload = rawPayload as StudentScheduleApiPayload;
        const items = buildScheduleLessonsFromStudentApi(schedulePayload);
        const enrichedItems = await enrichScheduleWithMakeups(items);
        setGeneratedAt(schedulePayload.generatedAt || null);
        setScheduleItems(enrichedItems);

        if (!enrichedItems.length) {
          setEmptyMessage("В вашем расписании пока нет уроков.");
          setStatus("empty");
          return;
        }

        setStatus("ready");
        return;
      }

      if (
        !canUseLegacyMatrixFallback(
          studentScheduleResult.status,
          studentScheduleResult.responsePayload,
          auth.session.token,
        )
      ) {
        setStatus("error");
        setErrorMessage(
          `Не удалось загрузить расписание (${studentScheduleResult.status}): ${extractPayloadMessage(
            studentScheduleResult.responsePayload,
            studentScheduleResult.error ?? "Неизвестная ошибка",
          )}`,
        );
        return;
      }

      const flowNo = auth.session.user.flowNo?.trim();
      if (!flowNo) {
        setStatus("error");
        setErrorMessage(
          "Backend не поддерживает `GET /students/me/schedule`, а fallback matrix недоступен: у студента не задан flowNo.",
        );
        return;
      }

      const apiResult = await apiClient.request({
        method: "GET",
        path: "/ops/console/attendance/matrix",
        query: {
          flowNo,
        },
      });

      if (!apiResult.ok) {
        if (apiResult.status === 404) {
          setEmptyMessage("Для вашего потока пока нет уроков в расписании.");
          setStatus("empty");
          return;
        }

        setStatus("error");
        setErrorMessage(
          `Не удалось загрузить расписание (${apiResult.status ?? "network"}): ${extractPayloadMessage(
            apiResult.responsePayload,
            apiResult.error ?? "Неизвестная ошибка",
          )}`,
        );
        return;
      }

      const payload = apiResult.responsePayload;
      if (!payload || typeof payload !== "object") {
        setStatus("error");
        setErrorMessage(
          "API вернул пустой или некорректный payload расписания.",
        );
        return;
      }

      const matrix = payload as AttendanceMatrixPayload;
      const result = buildScheduleLessons(matrix, auth.session.user.id);
      const enrichedItems = await enrichScheduleWithMakeups(result.items);

      setGeneratedAt(matrix.generatedAt || null);
      setScheduleItems(enrichedItems);

      if (
        studentScheduleResult.status === 401 &&
        auth.session.token.startsWith("mock.")
      ) {
        setDataNote(
          "Показано расписание потока через fallback matrix: student JWT недоступен в mock auth режиме.",
        );
      }

      if (!result.hasStudentRow) {
        setDataNote(
          "Показано общее расписание потока. Персональные отметки посещаемости пока недоступны.",
        );
      }

      if (!enrichedItems.length) {
        setEmptyMessage(result.emptyReason ?? "Расписание пока пустое.");
        setStatus("empty");
        return;
      }

      setStatus("ready");
    } catch (error) {
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const cancelBookedMakeup = async () => {
    const requestId = bookingDetailsItem?.makeupRequestId;
    if (!requestId) {
      setBookingCancelStatus("error");
      setBookingCancelMessage("Не найдена заявка для отмены записи.");
      return;
    }

    setBookingCancelStatus("submitting");
    setBookingCancelMessage(null);

    const result = await apiClient.request({
      method: "PATCH",
      path: `/makeups/${requestId}/status`,
      body: {
        status: "open",
        offeredSlot: "",
      },
    });

    if (!result.ok) {
      setBookingCancelStatus("error");
      setBookingCancelMessage(
        `Не удалось отменить запись (${result.status ?? "network"}): ${extractPayloadMessage(
          result.responsePayload,
          result.error ?? "Неизвестная ошибка",
        )}`,
      );
      return;
    }

    setBookingCancelStatus("success");
    setBookingCancelMessage("Запись на отработку отменена.");
    setBookingDetailsItem(null);
    await loadSchedule();
    setActionMessage("Запись на отработку отменена. Можно выбрать новый слот.");
  };

  useEffect(() => {
    void loadSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.session, auth.status]);

  return (
    <>
      <h2 className="content-title">Расписание</h2>

      {actionMessage ? (
        <div
          className="booking-success spaced-bottom"
          role="status"
          aria-live="polite"
        >
          {actionMessage}
        </div>
      ) : null}

      {status === "loading" ? (
        <LoadingSpinner label="Загружаем расписание студента из API..." />
      ) : null}

      {status === "error" ? (
        <div className="alert">
          <p className="alert-text">
            {errorMessage ?? "Неизвестная ошибка расписания"}
          </p>
          <button
            type="button"
            className="action-button"
            onClick={() => void loadSchedule()}
          >
            Повторить попытку
          </button>
        </div>
      ) : null}

      {status === "empty" ? (
        <div className="empty-state" role="status" aria-live="polite">
          <div className="empty-state-icon" aria-hidden="true">
            🗓️
          </div>
          <p className="empty-state-text">{emptyMessage}</p>
        </div>
      ) : null}

      {status === "ready" ? (
        <section
          className="schedule-section"
          aria-label="Student schedule lessons"
        >
          {generatedAt ? (
            <p className="hint">
              Обновлено: {new Date(generatedAt).toLocaleString("ru-RU")}
            </p>
          ) : null}
          {dataNote ? <p className="hint">{dataNote}</p> : null}

          <ul className="schedule-list">
            {scheduleItems.map((item) => {
              const showAttendance = isLessonInPast(item.isoDate);
              const attendanceStatus = normalizeAttendanceStatus(
                item.attendanceLabel,
                item.makeupStatus,
              );
              const cardTone = resolveScheduleCardTone(
                showAttendance,
                attendanceStatus,
              );
              const isMakeupActionable =
                attendanceStatus === "Отсутствовал" &&
                Boolean(onNavigateToMakeup);
              const canOpenBookingDetails =
                attendanceStatus === "Записан на отработку";
              const isActionable = isMakeupActionable || canOpenBookingDetails;
              const attendanceButtonLabel =
                attendanceStatus === "Отсутствовал"
                  ? "Записаться на отработку"
                  : attendanceStatus;
              return (
                <li
                  key={item.id}
                  className={`schedule-card schedule-card--${cardTone}`}
                >
                  <div className="schedule-card-header">
                    <p className="schedule-date">
                      <time dateTime={item.isoDate ?? undefined}>
                        {item.dateLabel}
                      </time>
                    </p>
                    <p className="schedule-time">{item.timeLabel}</p>
                  </div>

                  <dl className="schedule-meta">
                    <div>
                      <dt>Поток</dt>
                      <dd>{item.flowNo}</dd>
                    </div>
                    <div>
                      <dt>Урок</dt>
                      <dd>{item.topic}</dd>
                    </div>
                    {showAttendance ? (
                      <div>
                        <dt>Посещаемость</dt>
                        <dd>
                          <button
                            type="button"
                            className={`schedule-attendance schedule-attendance-button is-known ${
                              isMakeupActionable ? "is-actionable" : ""
                            }`}
                            onClick={() => {
                              if (isMakeupActionable) {
                                onNavigateToMakeup?.(item.lessonNo);
                                return;
                              }

                              if (canOpenBookingDetails) {
                                setBookingCancelStatus("idle");
                                setBookingCancelMessage(null);
                                setBookingDetailsItem(item);
                              }
                            }}
                            disabled={!isActionable}
                          >
                            {attendanceButtonLabel}
                          </button>
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {bookingDetailsItem ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Подтверждение отмены записи на отработку"
          className="modal-backdrop"
          onClick={() => setBookingDetailsItem(null)}
        >
          <div
            className="panel modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="compact-title">Отменить запись на отработку?</h3>
            <p className="hint flush-top">
              Поток: {bookingDetailsItem.flowNo} · Урок:{" "}
              {bookingDetailsItem.lessonNo ?? "—"}
            </p>
            <ul className="compact-list">
              <li>Статус: Записан на отработку</li>
              <li>Номер заявки: {bookingDetailsItem.makeupRequestId ?? "—"}</li>
              <li>
                Слот:{" "}
                {bookingDetailsItem.offeredSlot
                  ? formatWallClockDateTime(bookingDetailsItem.offeredSlot)
                  : "Слот уточняется"}
              </li>
              <li>Тема: {bookingDetailsItem.topic}</li>
            </ul>

            {bookingCancelStatus === "error" ? (
              <div className="alert spaced-bottom">
                {bookingCancelMessage ?? "Ошибка отмены записи"}
              </div>
            ) : null}

            <div className="action-row">
              <button
                type="button"
                className="action-button"
                onClick={() => void cancelBookedMakeup()}
                disabled={bookingCancelStatus === "submitting"}
              >
                {bookingCancelStatus === "submitting"
                  ? "Отменяем запись..."
                  : "Да, отменить запись"}
              </button>
              <button
                type="button"
                className="action-button"
                onClick={() => setBookingDetailsItem(null)}
                disabled={bookingCancelStatus === "submitting"}
              >
                Назад
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export { Schedule };
export default Schedule;
